import { useState, useEffect } from "react";
import { 
  Dialog,
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Funil } from "./types";
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { 
  Grip, X, Plus, ArrowUp, ArrowDown, Edit,
  Paintbrush, Filter, BarChart4, FilterX,
  GitPullRequestDraft, ListFilter, PieChart,
  TrendingUp, Workflow, Star, Bookmark
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";

interface FunilDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  funil: Funil | null;
  onSave: (funil: Partial<Funil>) => void;
  onCancel: () => void;
}

// Array of predefined column background colors
const columnColors = [
  { name: "Padrão", value: "bg-background" },
  { name: "Cinza", value: "bg-muted" },
  { name: "Azul Claro", value: "bg-blue-50" },
  { name: "Verde Claro", value: "bg-green-50" },
  { name: "Amarelo Claro", value: "bg-yellow-50" },
  { name: "Laranja Claro", value: "bg-orange-50" },
  { name: "Vermelho Claro", value: "bg-red-50" },
  { name: "Roxo Claro", value: "bg-purple-50" },
];

const iconOptions = [
  { name: "Filtro", icon: Filter },
  { name: "Gráfico", icon: BarChart4 },
  { name: "Filtro Avançado", icon: FilterX },
  { name: "Rascunho", icon: GitPullRequestDraft },
  { name: "Lista", icon: ListFilter },
  { name: "Gráfico Pizza", icon: PieChart },
  { name: "Tendência", icon: TrendingUp },
  { name: "Workflow", icon: Workflow },
  { name: "Estrela", icon: Star },
  { name: "Favorito", icon: Bookmark },
];

export const FunilDialog = ({ 
  open, 
  onOpenChange, 
  funil, 
  onSave, 
  onCancel 
}: FunilDialogProps) => {
  const [nome, setNome] = useState("");
  const [icone, setIcone] = useState("Filter");
  const [colunas, setColunas] = useState<Array<{id: string, titulo: string, cor?: string}>>([]);
  const [novaColuna, setNovaColuna] = useState("");
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingTitle, setEditingTitle] = useState("");

  useEffect(() => {
    if (funil) {
      setNome(funil.nome);
      setIcone(funil.icone || "Filter");
      setColunas(funil.colunas.map(col => ({ 
        id: col.id, 
        titulo: col.titulo,
        cor: col.cor || "bg-background"
      })));
    } else {
      setNome("");
      setIcone("Filter");
      setColunas([
        { id: "lead", titulo: "Leads", cor: "bg-blue-50" },
        { id: "contato", titulo: "Em Contato", cor: "bg-purple-50" },
        { id: "proposta", titulo: "Proposta Enviada", cor: "bg-yellow-50" },
        { id: "negociacao", titulo: "Em Negociação", cor: "bg-orange-50" },
        { id: "fechado", titulo: "Fechado", cor: "bg-green-50" },
        { id: "perdido", titulo: "Perdido", cor: "bg-red-50" }
      ]);
    }
  }, [funil, open]);

  const handleSubmit = () => {
    if (!nome.trim()) {
      return;
    }

    // Mapear colunas para o formato esperado
    const formattedColunas = colunas.map(col => ({
      id: col.id,
      titulo: col.titulo,
      cor: col.cor,
      cards: funil?.colunas.find(c => c.id === col.id)?.cards || []
    }));

    onSave({
      nome,
      icone,
      colunas: formattedColunas
    });
  };

  const handleAddColumn = () => {
    if (!novaColuna.trim()) return;
    
    // Gerar um ID baseado no título (slug)
    const id = novaColuna.toLowerCase()
      .replace(/[^\w\s]/gi, '')
      .replace(/\s+/g, '_');
    
    setColunas([...colunas, { id, titulo: novaColuna, cor: "bg-background" }]);
    setNovaColuna("");
  };

  const handleRemoveColumn = (id: string) => {
    setColunas(colunas.filter(col => col.id !== id));
  };

  const handleMoveColumn = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) || 
      (direction === 'down' && index === colunas.length - 1)
    ) {
      return;
    }

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const newColunas = [...colunas];
    const coluna = newColunas[index];
    
    newColunas.splice(index, 1);
    newColunas.splice(newIndex, 0, coluna);
    
    setColunas(newColunas);
  };

  const startEditingTitle = (index: number) => {
    setEditingIndex(index);
    setEditingTitle(colunas[index].titulo);
  };

  const saveEditingTitle = () => {
    if (editingIndex === null || !editingTitle.trim()) {
      setEditingIndex(null);
      return;
    }

    const newColunas = [...colunas];
    newColunas[editingIndex].titulo = editingTitle;
    setColunas(newColunas);
    setEditingIndex(null);
  };

  const changeColumnColor = (index: number, cor: string) => {
    const newColunas = [...colunas];
    newColunas[index].cor = cor;
    setColunas(newColunas);
  };

  // Helper to render the selected icon
  const renderSelectedIcon = () => {
    const IconComponent = iconOptions.find(option => option.name === icone)?.icon || Filter;
    return <IconComponent className="h-4 w-4 mr-2" />;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{funil ? "Editar Funil" : "Novo Funil"}</DialogTitle>
          <DialogDescription>
            {funil ? "Edite as informações do funil existente." : "Configure um novo funil de projetos."}
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="nome">Nome do Funil</Label>
            <Input 
              id="nome" 
              value={nome} 
              onChange={(e) => setNome(e.target.value)}
              placeholder="Ex: Funil de Vendas Residenciais"
              className="transition-all duration-200"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="icone">Ícone</Label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline" 
                  className="w-full justify-start text-left font-normal"
                >
                  {renderSelectedIcon()}
                  <span>{icone}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="start">
                <div className="grid grid-cols-2 gap-2 p-2">
                  {iconOptions.map((option) => (
                    <DropdownMenuItem 
                      key={option.name}
                      onClick={() => setIcone(option.name)}
                      className={cn(
                        "flex items-center gap-2 cursor-pointer",
                        icone === option.name ? "bg-primary/20" : ""
                      )}
                    >
                      <option.icon className="h-4 w-4" />
                      <span>{option.name}</span>
                    </DropdownMenuItem>
                  ))}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          
          <div className="grid gap-2">
            <Label className="flex items-center justify-between">
              <span>Colunas do Funil</span>
              <Badge variant="outline" className="font-normal">
                {colunas.length} colunas
              </Badge>
            </Label>
            
            <div className="max-h-[300px] overflow-y-auto pr-2 rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10 text-center">Ordem</TableHead>
                    <TableHead>Título</TableHead>
                    <TableHead className="w-24 text-center">Cor</TableHead>
                    <TableHead className="w-28 text-center">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {colunas.map((coluna, index) => (
                    <TableRow key={coluna.id} className="group hover:bg-muted/30 transition-colors">
                      <TableCell className="w-10 text-center">
                        <div className="flex flex-col gap-1 items-center">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-6 w-6 opacity-50 group-hover:opacity-100 transition-opacity"
                            onClick={() => handleMoveColumn(index, 'up')}
                            disabled={index === 0}
                          >
                            <ArrowUp className="h-3 w-3" />
                          </Button>
                          <span className="text-xs text-muted-foreground">{index + 1}</span>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-6 w-6 opacity-50 group-hover:opacity-100 transition-opacity"
                            onClick={() => handleMoveColumn(index, 'down')}
                            disabled={index === colunas.length - 1}
                          >
                            <ArrowDown className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        {editingIndex === index ? (
                          <div className="flex items-center gap-2">
                            <Input
                              value={editingTitle}
                              onChange={(e) => setEditingTitle(e.target.value)}
                              onBlur={saveEditingTitle}
                              onKeyDown={(e) => e.key === 'Enter' && saveEditingTitle()}
                              className="text-sm h-8"
                              autoFocus
                            />
                          </div>
                        ) : (
                          <div 
                            className="flex items-center gap-2 cursor-pointer hover:text-primary transition-colors"
                            onClick={() => startEditingTitle(index)}
                          >
                            <Grip className="h-4 w-4 text-muted-foreground" />
                            <span>{coluna.titulo}</span>
                            <Edit className="h-3.5 w-3.5 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="w-24 text-center">
                        <div className="dropdown-container relative group">
                          <Button
                            variant="ghost"
                            size="sm"
                            className={cn(
                              "h-6 rounded-md transition-all border",
                              coluna.cor || "bg-background"
                            )}
                            onClick={(e) => {
                              e.currentTarget.nextElementSibling?.classList.toggle('hidden');
                            }}
                          >
                            <Paintbrush className="h-3.5 w-3.5 text-muted-foreground" />
                          </Button>
                          <div className="color-dropdown absolute z-50 hidden top-full left-0 bg-background border rounded-md shadow-md mt-1 p-1 w-28 grid grid-cols-3 gap-1">
                            {columnColors.map((color) => (
                              <div
                                key={color.value}
                                className={cn(
                                  "w-8 h-8 rounded-md cursor-pointer hover:ring-2 ring-primary transition-all flex items-center justify-center",
                                  color.value
                                )}
                                title={color.name}
                                onClick={(e) => {
                                  changeColumnColor(index, color.value);
                                  (e.currentTarget.parentElement as HTMLElement).classList.add('hidden');
                                }}
                              >
                                {coluna.cor === color.value && <div className="w-1.5 h-1.5 rounded-full bg-primary" />}
                              </div>
                            ))}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => handleRemoveColumn(coluna.id)}
                          disabled={colunas.length <= 2}
                          className="h-7 w-7 text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            
            <div className="flex gap-2 mt-2">
              <Input 
                value={novaColuna} 
                onChange={(e) => setNovaColuna(e.target.value)}
                placeholder="Nova coluna"
                onKeyDown={(e) => e.key === 'Enter' && handleAddColumn()}
                className="transition-all duration-200"
              />
              <Button 
                variant="outline" 
                size="icon" 
                onClick={handleAddColumn}
                className="transition-all duration-200 hover:bg-primary hover:text-primary-foreground"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              *As alterações em colunas não afetarão os cards existentes.
            </p>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} className="transition-all duration-200">
            Cancelar
          </Button>
          <Button onClick={handleSubmit} className="transition-all duration-200">
            {funil ? "Salvar Alterações" : "Criar Funil"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
